import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEnvelopeCircleCheck, faEye, faTrash, faWarehouse } from "@fortawesome/free-solid-svg-icons";
import { ReceivedInvoiceApi, ReceivedInvoiceDataType } from "@/api/ReceivedInvoiceApi";
import { formatMoneyValue } from "@utils/Currency";
import styles from "./style.module.scss";
import moment from "moment";
import { Alert } from "reactstrap";
import { getTenantId } from "@utils/Auth";
import { DocumentsIndexPubSub, RefreshDocumentIndexEvent } from "@utils/pubsubs/DocumentIndexPubSub";
import { OpenSendDocumentMessageAsPubSub, OpenViewRecievedEmailPubSub } from "@utils/pubsubs/PubSub";
import SendDocumentMessageAsOffCanvas from "@components/layout/Offcanvas/SendDocumentMessageAsOffCanvas";
import ViewRecievedEmailOffCanvas from "@components/layout/Offcanvas/ViewRecievedEmailOffCanvas";
import { showAlert } from "@utils/Alert";

const ReceivedInvoiceIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const [tenantPrefix, setTenantPrefix] = useState<string>("")
  const ACCEPTED_DOCUMENT_BY_HACIENDA = 1

  useEffect(() => {
    loadTenantPrefix()
  }, []);

  useEffect(() => {
    const unSubscribe = DocumentsIndexPubSub.subscribe((params: RefreshDocumentIndexEvent) => {
      if (params.modelName === "ReceivedInvoice") {
        modelRef.current?.refetch();
      }
    });
    return () => { unSubscribe() };
  }, []);

  const loadTenantPrefix = async () => {
    const tenantId = await getTenantId();
    setTenantPrefix(tenantId)
  }

  const deleteRecord = async (id: string) => {
    const { errors } = await ReceivedInvoiceApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'La factura no puedo ser eliminado');
    } else {
      showAlert('success', 'La factura fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  return (
    <PageContainer
      title="Recepcion de Documentos Electronico"
      backUrl="/user/dashboard-accounting"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/receivedInvoices/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
      secondaryActions={[
        {
          actionTitle: "Ver Documentos Procesados",
          href: "/user/invoices/holds"
        },
      ]}
    >

      <Alert color="primary">
        <b>Recepción automática de facturas electrónicas a través de Easypos</b>
        <p>
          Para mejorar la eficiencia en la gestión de tus documentos electrónicos, <b>Easypos</b> ofrece la opción de cargar automáticamente las facturas que se envíen al correo asignado a tu cuenta.
        </p>

        <p>
          A partir de ahora, cualquier factura electrónica enviada al correo <b>facturas.{tenantPrefix || ""}@easyposcr.com</b> será detectada y cargada automáticamente en tu cuenta de Easypos, sin necesidad de ingresarla manualmente.
        </p>

        <p>⚠️ Importante:
          Este correo debe utilizarse únicamente como copia (CC o BCC) al momento en que tus proveedores te envíen las facturas.
          No se recomienda utilizarlo como correo principal en los sistemas emisores, ya que podrías dejar de recibir directamente tus comprobantes fiscales.
        </p>

        <p>
          Esta funcionalidad permite una recepción más rápida, segura y organizada de tus facturas dentro del sistema Easypos, asegurando trazabilidad y cumplimiento con los requerimientos fiscales.
        </p>

      </Alert>
      <ModelIndex<ReceivedInvoiceDataType>
        ref={modelRef}
        // meiliConfig={{
        //   index: "receivedInvoices",
        //   sort: ["createdAt:desc"]
        // }}
        mandatoryFilters={[{
          or: [{ status: { eq: "PENDING" } }, { status: { eq: "ERROR" } }]
        }]}
        fetchData={(...args) => ReceivedInvoiceApi.fetchAll(...args)}
        filterProps={[
          {
            key: "sequence",
            filterType: "contains"
          },
          {
            key: "emisorNombre",
            filterType: "contains"
          },
          {
            key: "emisorIdentificacion",
            filterType: "contains"
          }
        ]}
        primaryProperties={[
          {

            key: (data: ReceivedInvoiceDataType) => data.sequence || "N/A",
            highlighted: true,
            className: styles.sequence,
            optionalHighlighted: (val: any, data: ReceivedInvoiceDataType) => { return data.status == "ERROR" || false },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            }
          },
          {
            key: (data: ReceivedInvoiceDataType) => data.emisorNombre || "Correo Sin Documento XML",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: "sequence",
            title: "Numero Consecutivo"
          },
          {
            key: (data) => {

              return formatMoneyValue(Number(data.total || 0), 1)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "4" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            onClick: (id, receivedInvoice) => {
              window.open(`/user/provider-Invoices/addWithInventory/${receivedInvoice.xmlInvoiceAttachmentId}`, 'addInventory')
            },
            isVisible: (receivedInvoice) => {
              return receivedInvoice.status !== "ERROR" && receivedInvoice.documentStatus == ACCEPTED_DOCUMENT_BY_HACIENDA
            },
            actionTitle: "Ingresar Factura e Inventario",
            iconProps: { icon: faWarehouse }
          },
          {
            onClick: (id, receivedInvoice) => {
              OpenSendDocumentMessageAsPubSub.publish({
                openOffCanvas: true,
                invoiceAttachmentId: `${receivedInvoice.xmlInvoiceAttachmentId}`
              })
            },
            isVisible: (receivedInvoice) => {
              return receivedInvoice.status !== "ERROR" && receivedInvoice.documentStatus == ACCEPTED_DOCUMENT_BY_HACIENDA
            },
            actionTitle: "Enviar Mensaje sobre Documento",
            iconProps: { icon: faEnvelopeCircleCheck }
          },
          {
            onClick: (id, receivedInvoice) => {
              OpenViewRecievedEmailPubSub.publish({
                openOffCanvas: true,
                emailId: `${receivedInvoice.receivedEmailId}`
              })
            },
            actionTitle: "Ver Correo",
            iconProps: { icon: faEye }
          },
          {
            actionTitle: "Eliminar Factura",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Factura",
              confirmMessage: "¿Seguro que desea eliminar esta Factura?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <SendDocumentMessageAsOffCanvas />
      <ViewRecievedEmailOffCanvas />
    </PageContainer >
  );
}

export default ReceivedInvoiceIndex;
